import { useRef, useState } from "react";
import { bool } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { Tables, hashCode, MAX_TEXT_DESCRIPTION_LENGTH } from "@repo/utils";
import {
  useEstimateStructure,
  usePresetFieldNames,
  useEstimateEditorStore,
  useEstimateEditorSettings,
  useVisibilityModeCellClassName,
  useTableExpandHandler,
  TableTitle,
  VisibilityButton,
} from "@tools";
import { Grid, Collapse } from "@material-ui/core";
import useStyle from "@tools/EstimateEditor/styles/CommonTableStyles.style";
import classnames from "classnames";
import { DescriptionEditor } from "./components/DescriptionEditor";
import { Delta } from "quill";


export const TextDescription = observer(({
  tableBreakdownNext,
  tableBreakdownPrev,
  isLastTable,
}) => {
  const {t} = useTranslation();
  const structure = useEstimateStructure();
  const { visibilityMode } = useEstimateEditorStore();
  const {
    isProposal,
    blockExpansions,
    showPdfCover,
    isPdfPrinterView,
  } = useEstimateEditorSettings();
  const { getFieldName } = usePresetFieldNames(isProposal);
  const classes = useStyle();
  
  const [focused, setFocused] = useState(false);
  const [chars, setChars] = useState(false);
  
  const quillRef = useRef(null);
  
  const { settings, visibility, textDescription } = structure;
  const { showTextDescription } = settings;
  const { hideTextDescription } = visibility;
  
  const className = useVisibilityModeCellClassName(hideTextDescription);
  
  const { handleOpen, ...collapseProps } = useTableExpandHandler({
    expandable: !blockExpansions,
    isOpened: showTextDescription,
    onExpand: settings.setShowTextDescription
  });
  
  const handleTextChange = (deltas, contentLength) => {
    setChars(contentLength)
    if(contentLength) {
      const combined = deltas.reduce((c, d) => c ? c.compose(d) : d, null);
      if(!combined.ops.length)
        return;
      structure.applyTextDescriptionDelta(combined)
    }
    else
      structure.setTextDescription(new Delta([{insert: "\n"}]));
  }
  
  const handleFocus = () => setFocused(true);
  const handleBlur = () => setFocused(false);
  
  const hasError = chars > MAX_TEXT_DESCRIPTION_LENGTH;
  
  return (
    <Grid
      item container
      id={Tables.DESCRIPTION}
      justifyContent="center"
      alignItems="center"
      className={classnames(
        "preset-paperTextDescription pb-6",
        classes.whiteContainer,
        tableBreakdownNext && classes.whiteContainerLast,
        tableBreakdownPrev && classes.whiteContainerFirst,
        isLastTable && !isPdfPrinterView && classes.paddingBottom,
      )}
    >
      <Grid item container justifyContent="flex-end" className={classnames("vCon", showPdfCover && "pdf-mode", isPdfPrinterView && "printer-mode", classes.root)}>
        <TableTitle
          disabled={visibilityMode && hideTextDescription}
          expandable={!blockExpansions}
          presetClass="textDescription"
          buttonPresetClass="preset-textDescriptionExpandButton"
          className={classnames(isPdfPrinterView && "printer-mode")}
          id={hashCode(Tables.DESCRIPTION)}
          title={getFieldName("pt-textDescription")}
          showContent={showTextDescription}
          onContentAction={handleOpen}
          action={<>
            { visibilityMode &&
              <VisibilityButton hidden={hideTextDescription} onChange={visibility.setHideTextDescription} />
            }
          </>}
        />
        <Collapse
          in={showTextDescription}
          timeout="auto"
          mountOnEnter
          unmountOnExit
          className={classnames(showPdfCover && "pdf-mode", isPdfPrinterView && "printer-mode", classes.collapse, className)}
          data-id={hashCode("cont")}
          data-t={ Tables.DESCRIPTION }
          data-p={hashCode(Tables.DESCRIPTION)}
          { ...collapseProps }
        >
          <Grid
            container
            justifyContent="center"
            wrap="nowrap"
            direction="column"
          >
            <DescriptionEditor
              ref={quillRef}
              name="estimateTextDescription"
              // onChange={handleChange}
              onTextChange={handleTextChange}
              placeholder={!isProposal ? t("proposal_parts.editor_sections.description") : undefined}
              focused={focused}
              value={textDescription}
              readOnly={isProposal}
              hasError={hasError}
              onFocus={handleFocus}
              onBlur={handleBlur}
              className="preset-textDescriptionContent"
            />
          </Grid>
          {
            chars > (MAX_TEXT_DESCRIPTION_LENGTH - 100) &&
            <Grid
              item container
              justifyContent="flex-end"
              className={classnames(
                hasError && "color-error"
              )}
            >
              {chars}{" / "}{MAX_TEXT_DESCRIPTION_LENGTH}
            </Grid>
          }
        </Collapse>
      </Grid>
    </Grid>
  );
});

TextDescription.propTypes = {
  tableBreakdownNext: bool,
  tableBreakdownPrev: bool,
  isLastTable: bool,
};
